import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Coffee, Search, X, Loader2, RotateCcw, DollarSign, Calendar, ShoppingCart, AlertCircle, Check, Printer } from 'lucide-react';
import cafeService from '../services/cafe';
import printProfileService from '../services/printProfile';
import { printReceipt } from '../utils/printReceipt';

const formatRupiah = (num) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);

const CafeSales = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [summary, setSummary] = useState({ totalPenjualan: 0, totalTransaksi: 0, totalItem: 0 });
  const [selectedSale, setSelectedSale] = useState(null);
  const [voidConfirm, setVoidConfirm] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [isVoiding, setIsVoiding] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [printProfile, setPrintProfile] = useState({ header1: '', header2: '', header3: '', footer1: '', footer2: '', footer3: '' });

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      const data = await cafeService.getSales(params);
      setSales(data.sales || []);
      if (data.summary) setSummary(data.summary);
      if (data.pagination) setPagination(data.pagination);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, dateRange]);

  useEffect(() => { fetchSales(); }, [fetchSales]);

  // Fetch print profile for reprint
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await printProfileService.getProfile();
        setPrintProfile({
          header1: data.header1 || '',
          header2: data.header2 || '',
          header3: data.header3 || '',
          footer1: data.footer1 || '',
          footer2: data.footer2 || '',
          footer3: data.footer3 || ''
        });
      } catch (err) { /* silent */ }
    };
    fetchProfile();
  }, []);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleVoid = async () => {
    if (!voidConfirm || isVoiding) return;
    setIsVoiding(true);
    try {
      await cafeService.voidSale(voidConfirm._id, voidReason);
      setVoidConfirm(null);
      setVoidReason('');
      showSuccess(`Penjualan ${voidConfirm.nomorNota} di-void`);
      await fetchSales();
      setSelectedSale(null);
    } catch (err) {
      setError(err.message);
      setVoidConfirm(null);
    } finally {
      setIsVoiding(false);
    }
  };

  const METODE_BADGE = {
    TUNAI: 'bg-green-100 text-green-700',
    QRIS: 'bg-blue-100 text-blue-700',
    TRANSFER: 'bg-purple-100 text-purple-700',
    KARTU: 'bg-orange-100 text-orange-700',
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Riwayat Penjualan Cafe</h2>
          <p className="mt-1 text-sm text-gray-500">Daftar transaksi penjualan</p>
        </div>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 animate-slide-up">
          <div className="flex items-center space-x-2"><Check className="w-5 h-5 text-green-500" /><p className="text-sm text-green-800">{success}</p></div>
        </div>
      )}

      {/* Summary & Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl bg-green-50"><DollarSign className="w-5 h-5 text-green-600" /></div>
            <div><p className="text-xs text-gray-500">Total Penjualan</p><p className="text-xl font-bold text-green-600">{formatRupiah(summary.totalPenjualan)}</p></div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl bg-blue-50"><ShoppingCart className="w-5 h-5 text-blue-600" /></div>
            <div><p className="text-xs text-gray-500">Total Transaksi</p><p className="text-xl font-bold text-blue-600">{summary.totalTransaksi}</p></div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl bg-amber-50"><Coffee className="w-5 h-5 text-amber-600" /></div>
            <div><p className="text-xs text-gray-500">Total Item Terjual</p><p className="text-xl font-bold text-amber-600">{summary.totalItem}</p></div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="px-4 py-3">
          <div className="grid grid-cols-2 gap-3">
            <input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} className="input-field text-sm" />
            <input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} className="input-field text-sm" />
          </div>
        </div>
      </div>

      {/* Sales List */}
      <div className="space-y-2">
        {loading ? (
          <div className="flex flex-col items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /><span className="mt-3 text-gray-500">Loading...</span></div>
        ) : sales.length > 0 ? (
          sales.map((sale, idx) => (
            <div key={sale._id} className="card p-4 hover:shadow-md transition-all animate-slide-up" style={{ animationDelay: `${idx * 20}ms` }}>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1" onClick={() => setSelectedSale(selectedSale?._id === sale._id ? null : sale)}>
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${sale.status === 'VOID' ? 'bg-gray-100' : 'bg-green-50'} flex items-center justify-center`}>
                    <ShoppingCart className={`w-5 h-5 ${sale.status === 'VOID' ? 'text-gray-400' : 'text-green-600'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{sale.nomorNota}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${METODE_BADGE[sale.metodePembayaran] || 'bg-gray-100 text-gray-700'}`}>{sale.metodePembayaran}</span>
                      {sale.status === 'VOID' && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700">VOID</span>}
                    </div>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{sale.items?.length} item</p>
                    <div className="flex items-center space-x-3 mt-1 text-xs text-gray-400">
                      <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />{format(new Date(sale.createdAt), 'dd MMM yyyy HH:mm')}</span>
                      <span>{sale.createdBy?.name}</span>
                    </div>

                    {/* Expanded details */}
                    {selectedSale?._id === sale._id && (
                      <div className="mt-3 bg-gray-50 rounded-xl p-3 space-y-1 animate-slide-up">
                        {sale.items?.map((item, i) => {
                          const hasDiskon = item.diskonTipe && item.diskon > 0;
                          const displaySubtotal = item.subtotalDiskon != null ? item.subtotalDiskon : item.subtotal;
                          return (
                            <div key={i}>
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600">{item.product?.nama} x{item.quantity}</span>
                                <span className="font-semibold">{formatRupiah(displaySubtotal)}</span>
                              </div>
                              {hasDiskon && (
                                <div className="text-right text-[10px] text-red-500">
                                  Diskon {item.diskonTipe === 'PERSEN' ? `${item.diskon}%` : formatRupiah(item.diskon)}
                                  {item.subtotal > displaySubtotal && ` (hemat ${formatRupiah(item.subtotal - displaySubtotal)})`}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {/* Discount summary */}
                        {(sale.diskonTotalRp > 0 || sale.items?.some(i => i.diskonTipe)) && (
                          <>
                            <hr className="border-gray-200 my-1" />
                            <div className="flex justify-between text-[10px] text-gray-400">
                              <span>Subtotal</span>
                              <span>{formatRupiah(sale.totalSebelumDiskon || sale.totalHarga)}</span>
                            </div>
                            {sale.items?.some(i => i.diskonTipe) && (
                              <div className="flex justify-between text-[10px] text-red-500">
                                <span>Diskon Item</span>
                                <span>-{formatRupiah((sale.totalSebelumDiskon || sale.totalHarga) - sale.totalHarga - (sale.diskonTotalRp || 0))}</span>
                              </div>
                            )}
                            {sale.diskonTotalRp > 0 && (
                              <div className="flex justify-between text-[10px] text-red-500">
                                <span>Diskon Total{sale.diskonTotalTipe === 'PERSEN' ? ` (${sale.diskonTotal}%)` : ''}</span>
                                <span>-{formatRupiah(sale.diskonTotalRp)}</span>
                              </div>
                            )}
                          </>
                        )}
                        <hr className="border-gray-200 my-1" />
                        <div className="flex justify-between text-xs font-bold">
                          <span>Total</span>
                          <span>{formatRupiah(sale.totalHarga)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Bayar</span><span>{formatRupiah(sale.bayar)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-green-600">
                          <span>Kembali</span><span>{formatRupiah(sale.kembalian)}</span>
                        </div>
                        {sale.status === 'VOID' && sale.voidReason && (
                          <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                            Alasan void: {sale.voidReason}
                          </div>
                        )}

                        {/* Reprint buttons */}
                        <div className="flex space-x-2 mt-3">
                          <button onClick={(e) => { e.stopPropagation(); printReceipt(sale, printProfile, '58'); }}
                            className="flex-1 px-2 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-[10px] font-semibold rounded-lg border border-blue-200 transition-all active:scale-95 flex items-center justify-center space-x-1">
                            <Printer className="w-3 h-3" /><span>Reprint 58mm</span>
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); printReceipt(sale, printProfile, '80'); }}
                            className="flex-1 px-2 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-[10px] font-semibold rounded-lg border border-blue-200 transition-all active:scale-95 flex items-center justify-center space-x-1">
                            <Printer className="w-3 h-3" /><span>Reprint 80mm</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right ml-3">
                  <p className={`text-lg font-bold ${sale.status === 'VOID' ? 'text-gray-400' : 'text-green-600'}`}>{formatRupiah(sale.totalHarga)}</p>
                  {sale.status !== 'VOID' && (
                    <button onClick={() => { setVoidConfirm(sale); setVoidReason(''); }}
                      className="mt-1 p-1.5 text-gray-400 hover:text-red-600 rounded-lg text-xs" title="Void">
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="card p-12 text-center">
            <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Belum ada penjualan</p>
            <p className="text-gray-400 text-sm mt-1">Transaksi penjualan akan muncul di sini</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-between items-center card px-4 py-3">
          <span className="text-sm text-gray-500">Page {pagination.currentPage} of {pagination.totalPages}</span>
          <div className="flex space-x-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn-secondary text-xs disabled:opacity-50">Previous</button>
            <button disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)} className="btn-secondary text-xs disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {/* Void Modal */}
      {voidConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in" onClick={() => { if (!isVoiding) setVoidConfirm(null); }}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto z-10 animate-scale-in p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2.5 rounded-xl bg-red-50"><RotateCcw className="w-5 h-5 text-red-600" /></div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Void Penjualan</h3>
                  <p className="text-xs text-gray-500">{voidConfirm.nomorNota}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <div className="flex justify-between text-sm mb-1"><span className="text-gray-500">Total</span><span className="font-bold text-red-600">{formatRupiah(voidConfirm.totalHarga)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Item</span><span>{voidConfirm.items?.length} produk</span></div>
              </div>
              <textarea value={voidReason} onChange={e => setVoidReason(e.target.value)}
                placeholder="Alasan void (opsional)..." rows={2}
                className="input-field w-full resize-none text-sm mb-4" disabled={isVoiding} />
              <div className="flex justify-end space-x-3">
                <button onClick={() => setVoidConfirm(null)} disabled={isVoiding} className="btn-secondary text-sm"><X className="w-4 h-4" /><span>Batal</span></button>
                <button onClick={handleVoid} disabled={isVoiding}
                  className="px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white text-sm font-semibold rounded-xl hover:shadow-md transition-all disabled:opacity-50">
                  {isVoiding ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Memproses...</span></>
                    : <><RotateCcw className="w-4 h-4" /><span>Void Transaksi</span></>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CafeSales;
